import { useEffect, useState, useReducer } from 'react';
import { useIsMounted } from './useIsMounted';
import { usePrevious } from './usePrevious';

interface IFetchState<T> {
    data?: T;

    // is loading data
    isFetching?: boolean;

    // error from request function or null
    error?: Error;

    // Abort controller to manually abort the request
    prevAbortSignal?: AbortController;
}

export interface IUseFetch<T, A extends any[]> extends IFetchState<T> {
    // request function to fetch data
    request?: (...args: A) => Promise<T>;
    clearResponse?: () => void;
}

export interface IUseFetchOptions {
    initialData?: any;
    resolve?: boolean;
    resolveCondition?: string[];
    isAbortable?: boolean;
    propagateErrors?: boolean;
}

// type IUseFetchFn = <T,A extends any[]>(fn: (...args:) => Promise<T>, options?: IUseFetchOptions<T>) => IUseFetch<T,A>;

export const useFetch =<T, A extends any[]>(
  fn: (...args: A) => Promise<T>,
  {
      initialData = null,
      resolve = false,
      resolveCondition = [],
      isAbortable = false,
      propagateErrors = false
  }: IUseFetchOptions = {},
  mockdata?: any,
): IUseFetch<T, A> => {
    const isMounted = useIsMounted();
    const [{ data, isFetching, error }, setState] = useReducer((s: IFetchState<T>, a: IFetchState<T>) =>
      ({ ...s, ...a }), { data: initialData, isFetching: resolve, error: null });
    const [abortSignal, setAbortSignal] = useState<AbortController>(null);
    const prevAbortSignal = usePrevious(abortSignal);
    const clearResponse = () => setState({ data: initialData });

    const request = async (...args: A) => {
        try {
            if (isAbortable && 'AbortController' in window) {
                if (prevAbortSignal) {
                    prevAbortSignal.abort();
                }
                const controller = new AbortController();
                setAbortSignal(controller);
                args.push(controller.signal);
            }
            // check if component is mounted, only then update state
            isMounted() && setState({ isFetching: true, error: null });
            let response;
            // if mockdata is present then mock an api return that
            if (mockdata) {
                response = await new Promise((resolve, reject) => {
                    setTimeout(() => {
                        resolve(mockdata);
                    }, 1000)
                })
            } else {
                response = await fn(...args);
            }
            isMounted() && setState({ data: response, isFetching: false, error: null });
            return response;
        } catch (err) {
            // Is AbortError: To ignore abortError
            const isAbortError = err && err.name === 'AbortError';
            isMounted() && setState({ data: null, isFetching: false, error: isAbortError ? null : err });
            if (propagateErrors && !isAbortError) {
                // ignore AbortError
                throw err;
            }
        } finally {
            isMounted() && setState({ isFetching: false });
        }
    };

    useEffect(() => {
        // if (resolve) {
        //     request();
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, resolveCondition);

    return {
        clearResponse,
        data,
        isFetching,
        request,
        prevAbortSignal,
        error
    };
};